import React, { useEffect, useState } from 'react'
import { TextBanner } from '@christies/react/components/Banners'
import { withParsedContext, AnchorValue, SelectValue } from '../withJSSContext'
import { RenderFields } from '../utils'
import { ComponentStatus } from '@christies/react/typings'
import { ButtonProps } from '@christies/react/dist/lib/components/ButtonsInputs/Button/Button.types'
import { loadElementAfterHydration } from '../loadElementAfterHydration'

interface Props {
  pageEditing: boolean
  parsedFields: {
    Title: string
    'Button Label': string
    'Button Link': AnchorValue
    'Use Customized Sign Up': boolean
  }
  parsedParams: {
    'Component Theme': SelectValue<'light' | 'grey' | 'dark'>
    'Button Variant': SelectValue<'primary' | 'secondary'>
  }
  parsedRouteFields: {
    'Story ID': string
  }

  parsedSitecoreContext: {
    'Discovery.Feature.Seo.CanonicalUrl': string
    'Discovery.Feature.Scripts.ExternalFastSignUp.ReferenceHash': 'string'
    'Discovery.Feature.Scripts.ExternalFastSignUp.AssetsUrl': 'string'
    'Discovery.Feature.Magazines.IsStoryPreview': boolean
    language: string
  }
}

const getAssetURL = (baseUrl: string, path: string, hash: string): string => {
  const url = `${baseUrl ?? ''}${path}`
  if (hash) {
    return `${url}?hash=${hash}`
  }
  return url
}

const synchronouslyLoadScripts = async (scripts: string[]): Promise<void> => {
  for (const src of scripts) {
    await loadElementAfterHydration('script', { defer: true, src })
  }
}

const MagazinesSignUpModule: React.FC<Props> = ({
  parsedParams,
  parsedFields,
  parsedRouteFields,
  parsedSitecoreContext,
  pageEditing,
}) => {
  const baseAssetsUrl = parsedSitecoreContext?.['Discovery.Feature.Scripts.ExternalFastSignUp.AssetsUrl']
  const referenceHash = parsedSitecoreContext?.['Discovery.Feature.Scripts.ExternalFastSignUp.ReferenceHash']
  const isPreviewOrEditingMode = pageEditing || parsedSitecoreContext?.['Discovery.Feature.Magazines.IsStoryPreview']
  //* Always display component in experience editor or in preview mode
  const [status, setStatus] = useState<ComponentStatus>(isPreviewOrEditingMode ? 'ready' : 'initial')

  const fastSignupCss = getAssetURL(baseAssetsUrl, '/css/FastSignup.min.css', referenceHash)
  const loadFastSignupJs = getAssetURL(baseAssetsUrl, '/js/LoadFastSignUp.js', referenceHash)
  const globalBundle = getAssetURL(baseAssetsUrl, '/Build/scripts/v3/global-bundle.min.js', referenceHash)

  //* load scripts synchronously after hydration
  useEffect(() => {
    if (!isPreviewOrEditingMode) {
      Promise.all([
        loadElementAfterHydration('link', { href: fastSignupCss, rel: 'stylesheet' }),
        synchronouslyLoadScripts([globalBundle, loadFastSignupJs]),
      ])
        .then(() => setStatus('ready'))
        .catch(() => setStatus('error'))
    }
  }, [])

  const customData = { 'data-id': parsedRouteFields?.['Story ID'] }
  const genericData = {
    'data-tag-id': parsedRouteFields?.['Story ID'],
    'data-tag-url': parsedSitecoreContext?.['Discovery.Feature.Seo.CanonicalUrl'],
    'data-tag-language': parsedSitecoreContext?.language,
    'data-pageurl': '',
    'data-depname': '',
    'data-tag-name': '',
    'data-id': '',
  }

  if (status === 'error') return null

  const buttonState: ButtonProps['state'] = status === 'ready' ? 'active' : 'loading'

  const textBannerProps = {
    title: parsedFields?.Title,
    theme: parsedParams?.['Component Theme']?.key,
    cta: {
      state: buttonState,
      children: parsedFields?.['Button Label'],
      variant: parsedParams?.['Button Variant']?.key,
      className: 'fsu_btn-toggle',
      ...(parsedFields?.['Use Customized Sign Up'] ? customData : genericData),
      'data-typecode': 'ART',
    },
  }

  return (
    <RenderFields parsedFields={parsedFields}>
      <TextBanner {...textBannerProps} componentName="fast-sign-up" />
    </RenderFields>
  )
}

export default withParsedContext(MagazinesSignUpModule as React.FC)
